import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, parse } from 'date-fns';

import { DateFormat } from '@app/constants';

import { BillCycleDaysPayload } from '@mfe/shared/schema-types';

import type { ErrorsObj, RuntimeError } from '../graphql';

type State = BillCycleDaysPayload & {
  loading: boolean;
  billingCycleDayOfMonthUpdateDate: string | null;
  error: ErrorsObj | RuntimeError | null;
  newBillCycleDay: number | null;
  oldBillCycleDay: number | null;
  isConfirmationModalOpen: boolean;
};

type SetBillCycleDaysAction = PayloadAction<BillCycleDaysPayload>;

export const initialBillCycleState: State = {
  canChangeBillDate: false,
  days: [],
  billingCycleDayOfMonthUpdateDate: null,
  // Reason why the user can't change the bill cycle day
  reason: null,
  loading: false,
  // API / client runtime error
  error: null,
  newBillCycleDay: null,
  oldBillCycleDay: null,
  isConfirmationModalOpen: false,
};

export const updateBillCycleSlice = createSlice({
  name: 'updateBillCycle',
  initialState: initialBillCycleState,
  reducers: {
    setBillCycleLoading: (state, { payload }: { payload: boolean }) => {
      state.loading = payload;
    },
    setBillCycleDays: (state, action: SetBillCycleDaysAction) => {
      state.canChangeBillDate = action.payload.canChangeBillDate;
      state.days = action.payload.days;
      state.reason = action.payload.reason;
      state.loading = false;
      state.error = null;

      if (action.payload.billingCycleDayOfMonthUpdateDate) {
        const parsedDate = parse(
          action.payload.billingCycleDayOfMonthUpdateDate ?? new Date().toString(),
          DateFormat.YEAR_MONTH_DAY,
          new Date()
        );
        const addDaysToLastUpdate = addDays(parsedDate, 35).toString();
        state.billingCycleDayOfMonthUpdateDate = addDaysToLastUpdate;
      }
    },
    setError: (state, { payload }: { payload: State['error'] }) => {
      state.error = payload;
      state.loading = false;
    },
    triggerBillCycleDayUpdate: (state, { payload }: { payload: number }) => {
      state.loading = true;
    },
    setNewBillCycleDay: (state, { payload }: { payload: number }) => {
      state.loading = false;
      state.newBillCycleDay = payload;
    },
    setOldBillCycleDay: (state, { payload }: { payload: number | null }) => {
      state.oldBillCycleDay = payload;
    },
    setIsConfirmationModalOpen: (state, { payload }: { payload: boolean }) => {
      state.isConfirmationModalOpen = payload;
    },
    resetUpdateBillCycle: () => initialBillCycleState,
  },
});

export const getBillCycleDays = createAction('updateBillCycle/getBillCycleDays');

export const {
  setBillCycleLoading,
  setBillCycleDays,
  setError: setUpdateBillCycleError,
  triggerBillCycleDayUpdate,
  setNewBillCycleDay,
  setOldBillCycleDay,
  setIsConfirmationModalOpen,
  resetUpdateBillCycle,
} = updateBillCycleSlice.actions;

export const selectUpdateBillCycle = (state: { updateBillCycle: State }): State => state.updateBillCycle;
